import { useOrganismTree } from './hooks/organisms';
import { SelectTree } from '@veupathdb/coreui';
import React, { ReactElement, useCallback, useState } from 'react';
import { edaServiceUrl, projectId, projectName } from './config';
import { useWdkService } from '@veupathdb/wdk-client/lib/Hooks/WdkServiceHook';
import { TreeBoxVocabNode } from '@veupathdb/wdk-client/lib/Utils/WdkModel';
import { Node } from '@veupathdb/wdk-client/lib/Utils/TreeUtils';
import { areTermsInString } from '@veupathdb/wdk-client/lib/Utils/SearchUtils';
import {
  DatasetDependency,
  DependencyInputProps,
} from '@veupathdb/user-datasets/lib';
import { SelectTreeStyleSpec } from '@veupathdb/coreui/lib/components/inputs/SelectTree/SelectTree';
import { ButtonStateStyleSpec } from '@veupathdb/coreui/lib/components/buttons';
import {
  DatasetFormConfig,
  DatasetTypeConfig,
} from '@veupathdb/user-datasets/lib/Common/Configuration';
import { buildRnaSeqRcDataFiles } from '@veupathdb/user-datasets/lib/Service/utils/rnaseq-rc-data-files';
import { useConfiguredSubsettingClient } from '@veupathdb/eda/src/lib/core/hooks/client';
import { useStudyMetadata } from '@veupathdb/eda/src/lib/core/hooks/study';
import { DatasetWorkspaceConfig } from '@veupathdb/user-datasets/lib/Common/Configuration/DatasetWorkspaceConfig';
import { EdaStudyLinks } from '@veupathdb/user-datasets/src/lib/Common/Configuration/DatasetWorkspaceConfig';
import { makeEdaRoute, makeMapRoute } from './routes';

/**
 * Type identifiers for dataset types that have client handling.
 */
const implementedUploadTypes = {
  biom: { name: 'biom', version: '1.0' },
  genelist: { name: 'genelist', version: '1.0' },
  isasimple: { name: 'isasimple', version: '1.0' },
  bigwigfiles: { name: 'bigwigfiles', version: '1.0' },
  rnaseq: { name: 'rnaseq', version: '1.0' },
  rnaseqrc: { name: 'rnaseqrc', version: '1.0' },
  phenotype: { name: 'phenotype', version: '1.0' },
};

export const UserDatasetWorkspaceConfig: DatasetWorkspaceConfig = {
  baseDatasetTypeConfigs: [
    {
      ...implementedUploadTypes.bigwigfiles,
      description: `Integrate your bigWig data into ${projectName}.`,
    },
    {
      ...implementedUploadTypes.biom,
      description: `Integrate your BIOM study data into ${projectName}.`,
    },
    {
      ...implementedUploadTypes.genelist,
      description: `Integrate your gene list into ${projectName}.`,
    },
    {
      ...implementedUploadTypes.isasimple,
      description: `Explore and visualize your data table in ${projectName}`,
    },
    {
      ...implementedUploadTypes.phenotype,
      description: `Integrate your phenotype data into ${projectName}.`,
    },
    {
      ...implementedUploadTypes.rnaseq,
      description: `Integrate your normalized RNA-Seq data into ${projectName}.`,
    },
    {
      ...implementedUploadTypes.rnaseqrc,
      description: `Integrate your RNA-Seq raw counts into ${projectName}, and perform DE analysis.`,
    },
  ],

  uploadFormConfigurators: [
    // bigwig
    [implementedUploadTypes.bigwigfiles, bigwigFormConfigurator],

    // biom
    [implementedUploadTypes.biom, biomFormConfigurator],

    // genelist
    [implementedUploadTypes.genelist, genelistFormConfigurator],

    // isasimple
    [implementedUploadTypes.isasimple, isasimpleFormConfigurator],

    // phenotype
    [implementedUploadTypes.phenotype, phenotypeFormConfigurator],

    // rnaseq
    [implementedUploadTypes.rnaseq, rnaseqFormConfigurator],

    // rnaseqrc
    [implementedUploadTypes.rnaseqrc, rnaseqRcFormConfigurator],
  ],

  fetchEdaStudyMetadata: useStudyMeta,
};

const DefaultFormTitle = 'Upload Dataset';

const StudyDesignVocab: readonly [string, string][] = [
  ['Cluster-randomized controlled trial', 'Experimental'],
  ['Quasi-experimental study', 'Experimental'],
  ['Randomized controlled/clinical trial', 'Experimental'],

  ['Case series study', 'Observational'],
  ['Case-control study', 'Observational'],
  ['Cohort study', 'Observational'],
  ['Cross-sectional study', 'Observational'],
  ['Ecological study', 'Observational'],
  ['Panel study', 'Observational'],
  ['Surveillance study', 'Observational'],

  ['Meta-analysis', 'Review'],

  ['Other', 'n/a'],
];

const wranglerDataHelp = (
  <div>
    <p>
      Column names must be unique, cannot contain newlines, but can contain
      spaces. Values are limited to 1000 characters. We will consider a column:
    </p>
    <ul>
      <li>a date, if their values follow the pattern YYYY-MM-DD;</li>
      <li>
        geographical longitude, latitude, if their column names are: latitude,
        lat, longitude, long (case insensitive);
      </li>
      <li>
        numeric when all values are numbers:
        <ul>
          <li>Use a dot for decimals; commas will be removed.</li>
          <li>Scientific format is allowed.</li>
        </ul>
      </li>
    </ul>
  </div>
);
const wranglerVarAttrHelp = (
  <div>
    <p style={{ marginTop: 0 }}>
      (Optional) Upload a variable attributes file describing the variables in
      the data file:
    </p>
    <ul>
      <li>in .csv, .tsv, or tab-delimited .txt format,</li>
      <li>
        with columns labeled (i) variable; (ii) label; (iii) definition, and
      </li>
      <li>with one row for every variable in the data file.</li>
    </ul>
    <p className="important-info-bold">
      A valid variable attributes file is required to make your dataset Public.
    </p>
  </div>
);
const textFilesHelp = (
  <p>
    <i>
      To prevent accented letters and other special characters from displaying
      incorrectly, text files should use UTF-8 encoding (recommended). UTF-16BE,
      UTF-16LE, ISO-8859-1 and Windows-1252 are also supported.
    </i>
  </p>
);

function bigwigFormConfigurator(
  dataType: DatasetTypeConfig
): DatasetFormConfig {
  return {
    dataType,
    verbiage: {
      formTitle: `Upload a ${dataType.vdiConfig.category} Dataset`,
    },
    dataInputConfig: {
      file: {
        enabled: true,
        helpText: (
          <div>Total uncompressed files cannot be greater than 1GB.</div>
        ),
      },
      helpText: () => (
        <div className="formInfo">
          <p>
            We accept .bw or .bigwig files in the{' '}
            <a href="https://genome.ucsc.edu/goldenpath/help/bigWig.html">
              bigWig format
            </a>
            . If you need to upload more than one file please make a compressed
            file with all your bigWig files (a .tar.gz, .tgz or .zip file).
          </p>
          <ul>
            <li>
              Each bigWig file must be mapped to the genome that you selected
              above.
            </li>
            <li>Each bigWig file cannot be &gt; 500MB.</li>
          </ul>
        </div>
      ),
    },
    dependencies: {
      required: true,
      renderInput: ReferenceGenomeDependency,
    },
    enableExperimentalOrganism: true,
  };
}

function biomFormConfigurator(dataType: DatasetTypeConfig): DatasetFormConfig {
  return {
    dataType,
    verbiage: {
      formTitle: `Upload a ${dataType.vdiConfig.category} Dataset`,
    },
    dataInputConfig: {
      file: { enabled: true },
      helpText: () => (
        <p className="formInfo">
          We accept any file in the{' '}
          <a href="http://biom-format.org">BIOM format</a>, either JSON-based
          (BIOM 1.0) or HDF5 (BIOM 2.0+).
          <br />
          <br />
          If possible, try including taxonomic information and rich sample
          details in your file. This will allow you to select groups of samples
          and create meaningful comparisons at a desired aggregation level,
          using our filtering and visualisation tools.
        </p>
      ),
    },
    datasetCharacteristics: {
      enable: true,
      studyDesignVocab: StudyDesignVocab,
    },
  };
}

function genelistFormConfigurator(
  dataType: DatasetTypeConfig
): DatasetFormConfig {
  return {
    dataType,
    verbiage: {
      formTitle: `Upload a ${dataType.vdiConfig.category} Dataset`,
    },
    enableExperimentalOrganism: true,
    dataInputConfig: {
      file: { enabled: true },
      result: {
        enabled: true,
        offerStrategyUpload: false,
        compatibleRecordTypes: {
          transcript: {
            reportName: 'attributesTabular',
            reportConfig: {
              attributes: ['primary_key'],
              includeHeader: false,
              attachmentType: 'plain',
              applyFilter: true,
            },
          },
        },
      },
      helpText: () => (
        <details>
          <summary>
            Instructions to upload your {dataType.vdiConfig.category} dataset
          </summary>
          <div className="formInfo">
            <p>
              Upload a file containing gene IDs. Gene IDs need to be valid and
              be separated by valid delimiters. Valid gene IDs should:
            </p>
            <ul>
              <li>include only these characters [a-zA-Z0-9().:_-],</li>
              <li>have at least one alphabetical character, and</li>
              <li>be at most 80 characters.</li>
            </ul>
            <p>
              Invalid IDs and duplicated IDs will be discarded. Valid delimiters
              are:
            </p>
            <ul>
              <li>white space (newline, space, tab),</li>
              <li>comma, and </li>
              <li>semi-colon.</li>
            </ul>
            {textFilesHelp}
          </div>
        </details>
      ),
    },
  };
}

function isasimpleFormConfigurator(
  dataType: DatasetTypeConfig
): DatasetFormConfig {
  return {
    dataType,
    verbiage: {
      formTitle: DefaultFormTitle,
      formInputs: {
        datasetProperties: {
          label: 'Variable Attributes File',
          helpText: function HelpText() {
            return <div className="formInfo">{wranglerVarAttrHelp}</div>;
          },
        },
      },
    },
    datasetCharacteristics: {
      enable: projectId === 'ClinEpiDB',
      studyDesignVocab: StudyDesignVocab,
    },
    enableExperimentalOrganism: true,
    dataInputConfig: {
      file: { enabled: true },
      helpText: () => (
        <details>
          <summary>
            Instructions to upload your {dataType.vdiConfig.category} dataset
          </summary>
          <div className="formInfo">
            <p>
              Upload a single data file in .csv, .tsv, or tab-delimited .txt
              format. Compressed (.zip) files are also supported.
            </p>
            <ul>
              <li>Have variables as columns, records as rows.</li>
              <li>The first row must be column names (= variable names).</li>
            </ul>
            {wranglerDataHelp}
            {textFilesHelp}
          </div>
        </details>
      ),
    },
  };
}

function phenotypeFormConfigurator(
  dataType: DatasetTypeConfig
): DatasetFormConfig {
  return {
    dataType,
    verbiage: {
      formTitle: `Upload a ${dataType.vdiConfig.category} Dataset`,
      formInputs: {
        datasetProperties: {
          label: 'Variable Attributes File',
          helpText: function HelpText() {
            return <div className="formInfo">{wranglerVarAttrHelp}</div>;
          },
        },
      },
    },
    enableExperimentalOrganism: true,
    dataInputConfig: {
      file: { enabled: true },
      helpText: () => (
        <details>
          <summary>
            Instructions to upload your {dataType.vdiConfig.category} dataset
          </summary>
          <div className="formInfo">
            <p>Upload a tab-delimited .txt file. The file should contain:</p>
            <ul>
              <li>a gene ID column with header "geneID", and</li>
              <li>at least one numeric column.</li>
            </ul>
            <p>
              The file will be rejected if there are duplicated IDs. Invalid IDs
              will be discarded. Valid gene IDs:
            </p>
            <ul>
              <li>include only these characters [a-zA-Z0-9().:_-],</li>
              <li>have at least one alphabetical character, and</li>
              <li>are at most 80 characters.</li>
            </ul>
            {wranglerDataHelp}
            {textFilesHelp}
          </div>
        </details>
      ),
    },
  };
}

function rnaseqFormConfigurator(
  dataType: DatasetTypeConfig
): DatasetFormConfig {
  return {
    dataType,
    verbiage: {
      formTitle: `Upload a ${dataType.vdiConfig.category} Dataset`,
    },
    dataInputConfig: {
      file: {
        enabled: true,
        helpText: (
          <div>Total uncompressed files cannot be greater than 1GB.</div>
        ),
      },
      helpText: () => (
        <details>
          <summary>
            Instructions to upload your {dataType.vdiConfig.category} dataset
          </summary>
          <div className="formInfo">
            <p>Prepare your data for upload:</p>
            <ul>
              <li>
                Compress the files into a .tar.gz, .tgz or .zip archive file.
              </li>
              <li>
                Add files directly to the archive (do not add any folders).
              </li>
              <li>Ensure all files are non-empty.</li>
            </ul>
            <p>Include the following files in your archive:</p>
            <ol>
              <li>
                <b>Counts files</b> <i>(required)</i>
                <ul>
                  <li>
                    Provide one tab-delimited per sample (any or no extension).
                  </li>
                  <li>
                    Each file must provide exactly two columns, labeled:
                    <ul>
                      <li>'Gene ID' (or similar) and</li>
                      <li>'FPKM' or 'TPM' (lower case also accepted).</li>
                    </ul>
                  </li>
                  <li>
                    Gene IDs must be valid VEuPathDB IDs or valid aliases;
                    transcript IDs are also allowed, but not a mix of genes IDs
                    and transcript IDs.
                  </li>
                </ul>
              </li>
              <li>
                <b>Manifest file</b> <i>(required)</i>
                <ul>
                  <li>Provide a tab-delimited file named 'manifest.txt'.</li>
                  <li>
                    Include three or four unlabeled columns in this order:
                    <ul>
                      <li>sample name,</li>
                      <li>counts file name (must match exactly),</li>
                      <li>
                        strandedness ('unstranded' or 'stranded'),{' '}
                        <i>(only 'unstranded' is currently supported.)</i>, and
                      </li>
                      <li>
                        bigwig file name (optional, must match exactly if
                        provided).
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li>
                <b>bigWig files</b> <i>(optional)</i> for genome browser
                visualization.
                <ul>
                  <li>Optionally provide one .bw (bigWig) file per sample.</li>
                  <li>
                    If included, ensure each file is listed in the 4th column of
                    the manifest.
                  </li>
                </ul>
              </li>
            </ol>
            {textFilesHelp}
          </div>
        </details>
      ),
    },
    dependencies: {
      required: true,
      renderInput: ReferenceGenomeDependency,
    },
    enableExperimentalOrganism: true,
  };
}

function rnaseqRcFormConfigurator(
  dataType: DatasetTypeConfig
): DatasetFormConfig {
  return {
    dataType,
    verbiage: {
      formTitle: 'Upload an RNA-Seq Raw Counts Dataset',
      formInputs: {
        samplesDescription: {
          label: (
            <>
              Sample Details
              <br />
              (for AI annotation)
            </>
          ),
        },
      },
    },
    dataInputConfig: {
      file: {
        enabled: true,
        slots: [
          {
            label: 'Data file 1',
            buttonText: 'Choose sense or unstranded file',
            required: true,
          },
          {
            label: 'Data file 2',
            buttonText: 'Choose anti-sense file (optional)',
            required: false,
          },
        ],
      },
      helpText: () => (
        <details>
          <summary>
            Instructions to upload your {dataType.vdiConfig.category} dataset
          </summary>
          <div className="formInfo">
            <p>
              Upload your RNA-Seq count data as tab- or comma-delimited files
              (.txt, .tsv, .csv or .tab).
            </p>
            <p>
              If unstranded, provide a single unstranded count file as Data file
              1. If stranded, provide two files: sense as Data file 1 and
              anti-sense as Data file 2.
            </p>
            <p>Each count file should have:</p>
            <ul>
              <li>
                a first row that names all the columns. The first column must be
                the gene ID. Its name doesn't matter. The rest of the columns
                are the names for each sample. These names become that sample's
                ID, and for a stranded pair the same sample names must be used,
                in the same position, in both files;
              </li>
              <li>
                one row per gene, with a gene ID in the first column and whole,
                non-negative numbers for per sample counts (no decimals, no
                negative numbers, no commas).
              </li>
            </ul>
            <p>
              If your pipeline gives you multiple files instead (one per
              sample), merge them into the multi-column format first - matching
              genes by their ID, not by row position. An AI coding assistant can
              write this merge script for you if you ask it to join on gene ID
              and check that no genes are lost or duplicated.
            </p>
            <p>
              We use the VEuPathDB AI Metadata Analyzer to read your{' '}
              <b>Sample Details</b> together with the sample names in your count
              files. It automatically describes your samples to let you and
              other users compare groups (for example, treated vs. control) in
              your analysis.
            </p>
            {textFilesHelp}
          </div>
        </details>
      ),
    },
    dependencies: {
      required: true,
      renderInput: ReferenceGenomeDependency,
    },
    enableExperimentalOrganism: true,
    prepareDataFiles: (files, details) =>
      buildRnaSeqRcDataFiles(
        files,
        details.samplesDescription,
        dataType.vdiConfig.allowedFileExtensions
      ),
  };
}

function ReferenceGenomeDependency({
  dependencies,
  setDependencies,
}: DependencyInputProps): ReactElement | null {
  const popoverStyle: Partial<ButtonStateStyleSpec> = {
    dropShadow: {
      offsetX: 'none',
      offsetY: '',
      blurRadius: '',
      color: '',
    },
    border: {
      color: '#afafaf',
      width: 1,
      style: 'solid',
    },
  };

  const styleOverrides: SelectTreeStyleSpec = {
    treeNode: {
      labelTextWrapper: {
        fontSize: '1.1em',
      },
    },

    truncateSelectionWidth: '500px',

    popoverButton: {
      container: {
        marginBottom: '1em',
      },
      default: popoverStyle,
      disabled: popoverStyle,
      hover: popoverStyle,
      pressed: popoverStyle,
    },
  };

  const selectedList = dependencies?.map((entry) => entry.resourceDisplayName);

  const organismTree = useOrganismTree(true);

  const fileNameByTerm = useWdkService(async (wdkService) => {
    const genomeDataTypesResult = await wdkService.getAnswerJson(
      {
        searchName: 'GenomeDataTypes',
        searchConfig: { parameters: {} },
      },
      {
        attributes: ['organism_full', 'name_for_filenames'],
        pagination: {
          numRecords: -1,
          offset: 0,
        },
      }
    );
    return new Map(
      genomeDataTypesResult.records.map((rec) => [
        rec.attributes.organism_full as string,
        rec.attributes.name_for_filenames as string,
      ])
    );
  }, []);
  const buildNumber = useWdkService(async (wdkService) => {
    const config = await wdkService.getConfig();
    return config.buildNumber;
  }, []);
  const onSelectionChange = useCallback(
    function handleChange(selection: string[]) {
      if (fileNameByTerm == null || buildNumber == null) return;
      const dependencies = selection
        .map((term) => {
          const fileName = fileNameByTerm.get(term);
          return fileName == null
            ? undefined
            : {
                resourceDisplayName: term,
                resourceIdentifier: `${projectId}-${buildNumber}_${fileName}_Genome`,
                resourceVersion: buildNumber,
              };
        })
        .filter((dep): dep is DatasetDependency => dep != null);
      setDependencies(dependencies);
    },
    [buildNumber, fileNameByTerm, setDependencies]
  );
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  if (organismTree == null) return null;

  return (
    <SelectTree
      shouldCloseOnSelection
      buttonDisplayContent="Choose reference genome"
      tree={organismTree}
      getNodeId={getNodeId}
      getNodeChildren={getNodeChildren}
      onExpansionChange={setExpandedNodes}
      expandedList={expandedNodes}
      isMultiPick={false}
      isSearchable
      searchTerm={searchTerm}
      onSearchTermChange={setSearchTerm}
      searchPredicate={searchPredicate}
      isSelectable
      selectedList={selectedList}
      onSelectionChange={onSelectionChange}
      linksPosition={SelectTree.LinkPlacement.Top}
      styleOverrides={styleOverrides}
    />
  );
}

function getNodeId(node: Node<TreeBoxVocabNode>) {
  return node.data.term;
}
function getNodeChildren(node: Node<TreeBoxVocabNode>) {
  return node.children;
}
function searchPredicate(node: Node<TreeBoxVocabNode>, terms: string[]) {
  return areTermsInString(terms, node.data.display);
}

/**
 * Ugly hack to allow the use of a hook in the dataset management page class
 * components.
 */
function useStudyMeta(wdkDatasetId: string): EdaStudyLinks {
  const edaClient = useConfiguredSubsettingClient(edaServiceUrl);
  const edaStudyMetadata = useStudyMetadata(wdkDatasetId, edaClient).value;

  return {
    workspaceUrl: `${makeEdaRoute(wdkDatasetId)}/new`,
    mapUrl: edaStudyMetadata?.hasMap
      ? `${makeMapRoute(wdkDatasetId)}/new`
      : undefined,
  };
}
